import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Wrench, Users, CheckCircle, Clock, AlertTriangle, Package, ClipboardList, AlertCircle, Calendar, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { filterJobsByPermissions } from '../../utils/permissions';
import { CompetitionZoneWidget } from '../../components/widgets/CompetitionZoneWidget';
import { RhinoChallengesWidget } from '../../components/widgets/RhinoChallengesWidget';
import { SavedViewsDropdown } from '../../components/SavedViewsDropdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Operations stages
const OPS_STAGES = ['demo', 'pending_signature', 'signed_agreement', 'scheduled', 'prep', 'production', 'installation', 'final_inspection', 'complete'];

export const OperationsDashboard = () => {
  const { user, viewAsRole, viewAsUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState({
    activeJobs: 0,
    scheduledJobs: 0,
    inProduction: 0,
    completedToday: 0,
    crewUtilization: 0,
    avgCompletionTime: 0
  });
  
  // Work Order stats
  const [woStats, setWoStats] = useState(null);
  const [woLoading, setWoLoading] = useState(true);
  const [woError, setWoError] = useState(null);

  useEffect(() => {
    fetchData();
    fetchWorkOrderStats();
  }, []);

  useEffect(() => {
    applyRoleBasedFilter();
  }, [allJobs, user, viewAsRole, viewAsUser, users]);
  
  const fetchWorkOrderStats = async () => {
    setWoLoading(true);
    setWoError(null);
    try {
      const res = await axios.get(`${API}/ops/work-orders/stats`);
      setWoStats(res.data);
    } catch (err) {
      console.error('Failed to fetch work order stats:', err);
      setWoError('Failed to load');
    } finally {
      setWoLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [jobsRes, usersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`)
      ]);
      
      setAllJobs(jobsRes.data);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error('Failed to fetch operations data:', error);
      toast.error('Failed to load operations data');
    } finally {
      setLoading(false);
    }
  };

  const applyRoleBasedFilter = () => {
    if (!allJobs || allJobs.length === 0) {
      setJobs([]);
      return;
    }

    let effectiveUser = user;
    if (viewAsUser && viewAsRole) {
      const testUser = users.find(u => u.id === viewAsUser);
      if (testUser) effectiveUser = testUser;
    }

    const filteredJobs = filterJobsByPermissions(allJobs, effectiveUser, users);
    setJobs(filteredJobs);
    calculateStats(filteredJobs);
  };

  const calculateStats = (jobsData) => {
    const opsJobs = jobsData.filter(job => OPS_STAGES.includes(job.stage));
    
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const activeJobs = opsJobs.filter(j => 
      ['scheduled', 'prep', 'production', 'installation'].includes(j.stage)
    ).length;
    
    const scheduledJobs = opsJobs.filter(j => j.stage === 'scheduled').length;
    const inProduction = opsJobs.filter(j => 
      ['production', 'installation'].includes(j.stage)
    ).length;
    
    const completedToday = opsJobs.filter(j => 
      j.stage === 'complete' && new Date(j.updated_at) >= todayStart
    ).length;
    
    // Calculate crew utilization (placeholder - would need crew data)
    const totalCrews = teams.length || 1;
    const activeCrews = Math.min(inProduction, totalCrews);
    const crewUtilization = totalCrews > 0 ? ((activeCrews / totalCrews) * 100).toFixed(0) : 0;
    
    setStats({
      activeJobs,
      scheduledJobs,
      inProduction,
      completedToday,
      crewUtilization,
      avgCompletionTime: 0 // Placeholder
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground dark:text-slate-400">Loading operations...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground dark:text-white">Operations Dashboard</h1>
            <p className="text-muted-foreground dark:text-slate-400">Track production, crews, and job completion</p>
          </div>
          <SavedViewsDropdown
            dashboardType="operations"
            currentFilters={{}}
            onLoadView={(filters) => {}}
          />
        </div>

        {/* KPI Cards with Blue Glow Effect */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Active Jobs */}
          <Card 
            className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Active Jobs</CardTitle>
              <Wrench className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">{stats.activeJobs}</div>
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">In progress</p>
            </CardContent>
          </Card>

          {/* Scheduled Jobs */}
          <Card 
            className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Scheduled</CardTitle>
              <Clock className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">{stats.scheduledJobs}</div>
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">Awaiting start</p>
            </CardContent>
          </Card>

          {/* In Production */}
          <Card 
            className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">In Production</CardTitle>
              <Package className="h-4 w-4 text-orange-500 dark:text-orange-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">{stats.inProduction}</div>
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">Active work</p>
            </CardContent>
          </Card>

          {/* Completed Today */}
          <Card 
            className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Completed Today</CardTitle>
              <CheckCircle className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">{stats.completedToday}</div>
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">Jobs finished</p>
            </CardContent>
          </Card>
        </div>

        {/* Work Orders Section */}
        <div className="space-y-4" data-testid="work-orders-section">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-foreground dark:text-white flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-500" />
              Work Orders
            </h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/operations-hub/work-orders')}
              className="text-blue-500 hover:text-blue-400 hover:bg-blue-500/10"
            >
              View All
              <ExternalLink className="h-4 w-4 ml-1" />
            </Button>
          </div>
          
          {woLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[...Array(5)].map((_, i) => (
                <Card key={i} className="dark:bg-slate-800/60 dark:border-slate-700 animate-pulse">
                  <CardHeader className="pb-2">
                    <div className="h-4 bg-slate-700 rounded w-16"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-8 bg-slate-700 rounded w-12 mb-1"></div>
                    <div className="h-3 bg-slate-700 rounded w-20"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : woError ? (
            <Card className="dark:bg-slate-800/60 dark:border-slate-700">
              <CardContent className="py-8 text-center">
                <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                <p className="text-slate-400">{woError}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchWorkOrderStats} 
                  className="mt-3"
                >
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : woStats && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {/* Open */}
              <Card 
                className="dark:bg-slate-800/60 dark:border-slate-700 cursor-pointer hover:border-slate-500 transition-all"
                onClick={() => navigate('/operations-hub/work-orders?status=open')}
                data-testid="wo-card-open"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium dark:text-slate-300">Open</CardTitle>
                  <ClipboardList className="h-4 w-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold dark:text-white">{woStats.open_count}</div>
                  <p className="text-xs text-muted-foreground dark:text-slate-500">work orders</p>
                </CardContent>
              </Card>
              
              {/* Scheduled */}
              <Card 
                className="dark:bg-slate-800/60 dark:border-slate-700 cursor-pointer hover:border-blue-500/50 transition-all"
                onClick={() => navigate('/operations-hub/work-orders?status=scheduled')}
                data-testid="wo-card-scheduled"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium dark:text-slate-300">Scheduled</CardTitle>
                  <Calendar className="h-4 w-4 text-blue-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold dark:text-white">{woStats.scheduled_count}</div>
                  <p className="text-xs text-muted-foreground dark:text-slate-500">work orders</p>
                </CardContent>
              </Card>
              
              {/* In Progress */}
              <Card 
                className="dark:bg-slate-800/60 dark:border-slate-700 cursor-pointer hover:border-amber-500/50 transition-all"
                onClick={() => navigate('/operations-hub/work-orders?status=in_progress')}
                data-testid="wo-card-in-progress"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium dark:text-slate-300">In Progress</CardTitle>
                  <Clock className="h-4 w-4 text-amber-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold dark:text-white">{woStats.in_progress_count}</div>
                  <p className="text-xs text-muted-foreground dark:text-slate-500">work orders</p>
                </CardContent>
              </Card>
              
              {/* Overdue */}
              <Card 
                className={`dark:bg-slate-800/60 cursor-pointer transition-all ${woStats.overdue_count > 0 ? 'border-red-500/50 hover:border-red-500' : 'dark:border-slate-700 hover:border-slate-500'}`}
                onClick={() => navigate('/operations-hub/work-orders?overdue=true')}
                data-testid="wo-card-overdue"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium dark:text-slate-300">Overdue</CardTitle>
                  <AlertCircle className={`h-4 w-4 ${woStats.overdue_count > 0 ? 'text-red-400' : 'text-slate-400'}`} />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${woStats.overdue_count > 0 ? 'text-red-400' : 'dark:text-white'}`}>
                    {woStats.overdue_count}
                  </div>
                  <p className="text-xs text-muted-foreground dark:text-slate-500">past due</p>
                </CardContent>
              </Card>
              
              {/* Urgent */}
              <Card 
                className={`dark:bg-slate-800/60 cursor-pointer transition-all ${woStats.urgent_count > 0 ? 'border-red-500/50 hover:border-red-500' : 'dark:border-slate-700 hover:border-slate-500'}`}
                onClick={() => navigate('/operations-hub/work-orders?priority=urgent')}
                data-testid="wo-card-urgent"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium dark:text-slate-300">Urgent</CardTitle>
                  <AlertTriangle className={`h-4 w-4 ${woStats.urgent_count > 0 ? 'text-red-400' : 'text-slate-400'}`} />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${woStats.urgent_count > 0 ? 'text-red-400' : 'dark:text-white'}`}>
                    {woStats.urgent_count}
                  </div>
                  <p className="text-xs text-muted-foreground dark:text-slate-500">priority</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Stage Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="dark:bg-slate-800/60 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="dark:text-white">Job Status Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { stage: 'scheduled', label: 'Scheduled', color: 'bg-blue-500' },
                  { stage: 'prep', label: 'Prep', color: 'bg-purple-500' },
                  { stage: 'production', label: 'Production', color: 'bg-orange-500' },
                  { stage: 'installation', label: 'Installation', color: 'bg-yellow-500' },
                  { stage: 'final_inspection', label: 'Final Inspection', color: 'bg-emerald-500' },
                  { stage: 'complete', label: 'Complete', color: 'bg-slate-500' }
                ].map(({ stage, label, color }) => {
                  const count = jobs.filter(j => j.stage === stage).length;
                  const totalOpsJobs = jobs.filter(j => OPS_STAGES.includes(j.stage)).length;
                  const percentage = totalOpsJobs > 0 ? ((count / totalOpsJobs) * 100).toFixed(0) : 0;
                  
                  return (
                    <div key={stage} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-foreground dark:text-slate-200">{label}</span>
                        <span className="text-muted-foreground dark:text-slate-400">{count} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                        <div 
                          className={`${color} h-2 rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="dark:bg-slate-800/60 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="dark:text-white">Operations Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-100 dark:border-blue-800">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <span className="font-medium dark:text-slate-200">Crew Utilization</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.crewUtilization}%</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg border border-emerald-100 dark:border-emerald-800">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="font-medium dark:text-slate-200">Jobs This Week</span>
                  </div>
                  <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.completedToday * 5}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-100 dark:border-orange-800">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                    <span className="font-medium dark:text-slate-200">Delayed Jobs</span>
                  </div>
                  <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">0</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Engagement Widgets Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CompetitionZoneWidget />
          <RhinoChallengesWidget />
        </div>
      </div>
    </Layout>
  );
};
